import React, { useCallback, useMemo } from 'react';
import Style from './timePicker.module.scss';
import { AppDropDownWithSuggestionInput } from 'uikit/inputs';
import { DropdownOption } from 'uikit/inputs/dropdown/appDropdownInput';
import { ReactComponent as TimeIcon } from 'assets/img/generalBuilder/clock_24.svg';
import { BuilderFieldError, BuilderFieldErrorProps, ErrorSign } from '../../wrappers/error/builderFieldError.component';
import { BuilderFieldLabel, BuilderFieldLabelProps } from '../../wrappers/label/builderFieldLabel.component';
import { getTimeInputOptions } from './timePickerOptions';

export type TimePickerProps = {
  minTimeInMills?: number;
  timeInMills?: number | null;
  onTimeChanged?: (newTime: number | null | undefined) => void;
  /*
  interval between generated time entries, e.g. 01:00 and 01:30
   */
  timeEntriesIntervalInMinutes?: number;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  invalid?: boolean;
  suggestion?: boolean;
  isRequired?: boolean;
};

export const TimePicker = React.forwardRef<HTMLInputElement, TimePickerProps>((props, ref) => {
  const {
    minTimeInMills,
    onTimeChanged,
    timeInMills,
    timeEntriesIntervalInMinutes = 30,
    className,
    disabled,
    placeholder,
    invalid,
    suggestion = true,
    isRequired,
  } = props;

  const timeEntries: DropdownOption<number>[] = useMemo(
    () => getTimeInputOptions(timeEntriesIntervalInMinutes, minTimeInMills ?? 0),
    [minTimeInMills, timeEntriesIntervalInMinutes],
  );

  const value = useMemo(() => {
    const index = timeEntries.findIndex((x) => x.key === timeInMills);
    if (index === -1) return undefined;
    return timeEntries[index];
  }, [timeEntries, timeInMills]);

  const onChange = useCallback(
    (selectedOption: DropdownOption<number> | undefined) => {
      onTimeChanged?.(selectedOption?.key ?? null);
    },
    [onTimeChanged],
  );

  return (
    <AppDropDownWithSuggestionInput<DropdownOption<number>>
      CaretIcon={TimeIcon}
      isRequired={isRequired}
      suggestion={suggestion}
      disabled={disabled}
      className={className}
      placeholder={placeholder}
      invalid={invalid}
      options={timeEntries}
      value={value}
      onChange={onChange}
    />
  );
});

export type AppTimePickerFieldProps = {
  labelProps: BuilderFieldLabelProps;
  errorProps?: BuilderFieldErrorProps;
} & TimePickerProps;

export const AppTimePickerField = React.forwardRef<HTMLInputElement, AppTimePickerFieldProps>(
  ({ errorProps, labelProps, disabled, ...rest }, ref) => {
    return (
      <BuilderFieldError {...errorProps}>
        <BuilderFieldLabel
          disabled={disabled}
          rightSection={
            errorProps?.errors ? (
              <div className={Style.rightSection}>
                <ErrorSign />
              </div>
            ) : (
              <></>
            )
          }
          {...labelProps}
        >
          <TimePicker
            ref={ref}
            disabled={disabled}
            invalid={!!errorProps?.errors || (!!errorProps?.errors?.length && errorProps?.errors?.length > 0)}
            {...rest}
          />
        </BuilderFieldLabel>
      </BuilderFieldError>
    );
  },
);
